import { captureException } from '@sentry/react';
import config from 'config';
import { differenceInDays, isValid, parseISO } from 'date-fns';
import { DirectSignupFilters, ResumedLeadState, useSignupDirectCtx } from 'domains/signupDirect/context';
import { ConsultationFormType } from 'domains/signupDirect/schemas/consultationSchema';
import { EvaluationFormType, LoginContactInfoType } from 'domains/signupDirect/schemas/evaluationSchema';
import { get } from 'lodash';
import { useEffect } from 'react';
import { DeepPartial, useFormContext } from 'react-hook-form';
import { EvaluationScheduleFormType } from '../schemas/evaluationScheduleSchema';
import { safeSessionStorage } from 'utils/storage/safeStorage';
import { isStrictISODate } from 'utils/date';
import { SignupError } from 'utils/errors/debuggableError';

const STORAGE_VERSION = config.get('buildCommitHash') ?? undefined;
const MAX_LIFETIME_DAYS = 1;

const LOCAL_STORAGE_KEYS: Record<string, string> = {
  FILTERS: 'directSignup-filters',
  STEP: 'directSignup-step',
  EVALUATION_TIME_SLOT: 'directSignup-evaluationTimeSlot',
  INCOMPLETE_EVAL_FORM: 'directSignup-incompleteEvalForm',
  INCOMPLETE_CONSULT_FORM: 'directSignup-incompleteConsultForm',
  SPEECH_THERAPY_SESSION_SCHEDULE: 'directSignup-speechTherapySessionSchedule',
  RESUMED_LEAD_STATE: 'directSignup-resumedLeadState',
  LOGIN_CONTACT_INFO: 'directSignup-loginContactInfo',

  TIMESTAMP: 'directSignup-timestamp',
  VERSION: 'directSignup-version',
};

const stringifyReplacer = (_: unknown, value: unknown) => {
  /** Note: do not save empty fields */
  return value === '' ? undefined : value;
};

const parseReplacer = (_: unknown, value: unknown) => {
  // Dates
  if (typeof value === 'string' && isStrictISODate(value)) {
    return parseISO(value);
  }
  return value;
};

type LocalStorageData = {
  filters?: DirectSignupFilters;
  incompleteEvalForm?: DeepPartial<EvaluationFormType>;
  incompleteConsultForm?: DeepPartial<ConsultationFormType>;
  evalTimeSlot?: EvaluationScheduleFormType;
  resumedLeadState?: ResumedLeadState;

  loginContactInfo?: LoginContactInfoType;

  timestamp?: Date;
  version?: string;
};

export const onClearSignupLocalStorage = () => {
  Object.keys(LOCAL_STORAGE_KEYS).forEach(key => {
    safeSessionStorage.removeItem(LOCAL_STORAGE_KEYS[key]);
  });
};

export const onSaveSignupToLocalStorage = ({
  filters,
  incompleteEvalForm,
  incompleteConsultForm,
  evalTimeSlot,
  resumedLeadState,
  loginContactInfo,
}: LocalStorageData) => {
  if (filters) safeSessionStorage.setItem(LOCAL_STORAGE_KEYS.FILTERS, JSON.stringify(filters, stringifyReplacer));
  if (incompleteEvalForm) {
    safeSessionStorage.setItem(
      LOCAL_STORAGE_KEYS.INCOMPLETE_EVAL_FORM,
      JSON.stringify(incompleteEvalForm, stringifyReplacer),
    );
  }
  if (evalTimeSlot) {
    safeSessionStorage.setItem(
      LOCAL_STORAGE_KEYS.EVALUATION_TIME_SLOT,
      JSON.stringify(evalTimeSlot, stringifyReplacer),
    );
  }
  if (incompleteConsultForm) {
    safeSessionStorage.setItem(
      LOCAL_STORAGE_KEYS.INCOMPLETE_CONSULT_FORM,
      JSON.stringify(incompleteConsultForm, stringifyReplacer),
    );
  }

  if (resumedLeadState) {
    safeSessionStorage.setItem(
      LOCAL_STORAGE_KEYS.RESUMED_LEAD_STATE,
      JSON.stringify(resumedLeadState, stringifyReplacer),
    );
  }

  if (loginContactInfo) {
    safeSessionStorage.setItem(
      LOCAL_STORAGE_KEYS.LOGIN_CONTACT_INFO,
      JSON.stringify(loginContactInfo, stringifyReplacer),
    );
  }

  safeSessionStorage.setItem(LOCAL_STORAGE_KEYS.TIMESTAMP, new Date().toISOString());
  if (STORAGE_VERSION) safeSessionStorage.setItem(LOCAL_STORAGE_KEYS.VERSION, STORAGE_VERSION);
};

export const resumeSignupFromLocalStorage = (): LocalStorageData => {
  const result: LocalStorageData = {};
  try {
    const savedFilters = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.FILTERS);
    const savedEvalTimeSlot = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.EVALUATION_TIME_SLOT);
    const savedIncompleteEvalForm = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.INCOMPLETE_EVAL_FORM);
    const savedIncompleteConsultForm = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.INCOMPLETE_CONSULT_FORM);
    const savedResumedLeadState = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.RESUMED_LEAD_STATE);
    const savedLoginContactInfo = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.LOGIN_CONTACT_INFO);
    const savedVersion = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.VERSION);
    const savedTimestamp = safeSessionStorage.getItem(LOCAL_STORAGE_KEYS.TIMESTAMP);

    if (!savedVersion || savedVersion !== STORAGE_VERSION || !savedTimestamp) {
      return {};
    }

    if (
      !isValid(parseISO(savedTimestamp)) ||
      Math.abs(differenceInDays(parseISO(savedTimestamp), new Date())) > MAX_LIFETIME_DAYS
    ) {
      return {};
    }

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters) as DirectSignupFilters;
      result.filters = parsedFilters;
    }

    if (savedEvalTimeSlot) {
      result.evalTimeSlot = JSON.parse(savedEvalTimeSlot, parseReplacer);
    }

    if (savedIncompleteEvalForm) {
      const cleanedData = JSON.parse(savedIncompleteEvalForm);
      result.incompleteEvalForm = cleanedData;
    }
    if (savedIncompleteConsultForm) {
      const cleanedData = JSON.parse(savedIncompleteConsultForm);
      result.incompleteConsultForm = cleanedData;
    }

    if (savedResumedLeadState) {
      result.resumedLeadState = JSON.parse(savedResumedLeadState);
    }

    if (savedLoginContactInfo) {
      result.loginContactInfo = JSON.parse(savedLoginContactInfo);
    }
  } catch (e) {
    captureException(
      new SignupError('Signup Failed to load saved data from localStorage', {
        debugData: {
          e,
          message: get(e, 'message'),
        },
      }),
    );
  }

  return result;
};

export const usePeriodicEvaluationFormSave = () => {
  const { onSaveIncompleteEvalForm } = useSignupDirectCtx();
  const { getValues } = useFormContext<EvaluationFormType>();

  useEffect(() => {
    const interval = setInterval(() => {
      onSaveIncompleteEvalForm(getValues());
    }, 1000 * 5); // 5 seconds

    return () => clearInterval(interval);
  }, [getValues, onSaveIncompleteEvalForm]);
};

export const usePeriodicConsultationFormSave = () => {
  const { onSaveIncompleteConsultForm } = useSignupDirectCtx();
  const { getValues } = useFormContext<ConsultationFormType>();

  useEffect(() => {
    const interval = setInterval(() => {
      onSaveIncompleteConsultForm(getValues());
    }, 1000 * 5); // 5 seconds

    return () => clearInterval(interval);
  }, [getValues, onSaveIncompleteConsultForm]);
};
